import { animate, style, transition, trigger } from '@angular/animations';
import { ENTER } from '@angular/cdk/keycodes';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { translate, translateObject } from '@jsverse/transloco';

import { Observable, Subject } from 'rxjs';

import { debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { Constants } from 'app/constants/constants';
import { DataSourceType, WidgetDataSource } from 'app/models';
import { QuickActionsItem } from 'app/modules/collection/services/documents-quick-action.service';
import { FilterService } from 'app/modules/collection/services/filter.service';
import { TagCacheService } from 'app/modules/dashboard/services/tag-cache.service';
import { LexDatePipe } from 'app/pipes/lex-date.pipe';
import { Account, SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { Session, AuthService } from 'app/providers/bridge-api/auth.service';
import {
  DocumentSearchType,
  DocumentViewSection,
  NlpOutputService,
} from 'app/providers/nlp-output.service';
import { DocumentAnalysis } from 'app/providers/storage-api/analysis.service';
import { AnnotationService } from 'app/providers/storage-api/annotation.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { Collection, CollectionService } from 'app/providers/storage-api/collection.service';
import { Document, DocumentService } from 'app/providers/storage-api/document.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { SentimentPolarityRange } from 'app/providers/storage-api/project.service';
import { StorageListResult, StoragePageRequest } from 'app/providers/storage-api.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { AccountUtils } from 'app/utils/account.utils';
import { SentimentUtils } from 'app/utils/sentiment.utils';
import { Utils } from 'app/utils/utils';
import {
  ContentChannel,
  ContentChannelService,
} from '../../../../../providers/storage-api/content-channel.service';
import { DocumentDeleteComponent } from '../document-delete/document-delete.component';
import { DocumentScoringMetricsOvertimeSettings } from '../document-scoring-metrics/document-scoring-metrics-overtime-section/document-scoring-metrics-overtime-section.component';

export interface DocumentViewOptions {
  collectionName: string;
  analysisName?: string;
  page?: StorageListResult<Document>;
  pageSupplier?: (request: StoragePageRequest<Document>) => Promise<StorageListResult<Document>>;
  pageIndex: number;
  pageItemSupplier?: (documentId: string) => Promise<Document>;
  documentIndex: number;
  limit: number;
  documentId: string;
  projectId?: string;
  entity?: string;
  entityDisplayName?: string;
  analyticItem?: string;
  sectionFilters?: { documentId: string; sectionId: number }[];
  sectionPropertySelected?: boolean;
  sentimentScale: number;
  sentimentDisplay: string;
  dashboardId: string;
  widgetDataSource?: WidgetDataSource;
  isDashboardLocked: boolean;
  sentimentRanges: DocumentViewSentimentRanges;
  showNlpData?: boolean;
  nlpFields?: { id; display }[];
  showMetadata?: boolean;
  metadataFields?: { id: string; display: boolean }[];
  dataSourceType?: DataSourceType;
  analysisGroupId?: string;
  isSocial?: boolean;
  hideOpenNewTabAction?: boolean;
  hideRightSplitArea?: boolean;
  showQuickActions?: boolean;
  quickActions?: QuickActionsItem[];
  staticFeatures?: any[];
  initialSearchTerm?: string;
  backAction?: { label: string };
}

export interface DocumentViewSentimentRanges {
  document: SentimentPolarityRange[];
  feature: SentimentPolarityRange[];
}

export interface SectionFilter {
  label: string;
  filterType: 'section' | string;
  filterBase: DocumentViewSection;
  sectionIds: any[];
}

const DOCUMENT_SUMMARY_TEXT_SIZE_MIN = 'DOCUMENT_SUMMARY_TEXT_SIZE_MIN';
const DOCUMENT_SUMMARY_TEXT_SIZE_MIN_DEFAULT = 100;

@EnableClassTelemetry()
@Component({
  selector: 'app-document-view',
  templateUrl: './document-view.component.html',
  styleUrls: ['./document-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('editorTrigger', [
      transition(':enter', [
        style({ left: '100%' }),
        animate('0.5s ease-in-out', style({ left: 0 })),
      ]),
      transition(':leave', [animate('0.5s ease-in-out', style({ left: '100%' }))]),
    ]),
  ],
  standalone: false,
})
export class DocumentViewComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() public options: DocumentViewOptions;
  @Input() public hasChanged: boolean;
  @Output() public close = new EventEmitter<boolean>();
  @Output() public hasChangedChange = new EventEmitter<boolean>();
  @Output() public documentIdChange = new EventEmitter<string>();
  @Output() pageChange = new EventEmitter<StorageListResult<Document>>();
  @Output() highlightChange = new EventEmitter<{ entity: string; analyticItem: string } | null>();

  @ViewChild('tagInput') tagInput: ElementRef;
  @ViewChild('tagAutocomplete') tagAutocomplete: MatAutocomplete;
  @ViewChild('tagScroll') tagScroll?: ElementRef;

  /**
   * Unique index for current document.
   */
  get computedIndex(): number {
    return this.pageIndex * this.limit + this.documentIndex;
  }

  get analysis(): DocumentAnalysis {
    return this.document?.analyses?.[0];
  }

  isReadOnly;
  isAlwaysRedact;
  hasLLMFeature;

  customFields: {
    id: string;
    longLabel: string;
    value: any;
    type: string;
    isAlias: boolean;
    pipeParams?: string;
  }[];

  private account: Account;

  isLoading = {
    page: false,
    document: true,
    documentSummary: false,
    save: false,
    editSentiment: false,
    editEntity: false,
    editTopic: false,
  };

  page: StorageListResult<Document> = {
    count: 0,
    items: [],
  };
  pageIndex = 0;
  limit = 20;
  documentIndex = 0;
  documentId: string;
  document: Document;
  closedGroups: string[] = [];
  features: any[] = [];
  goToDoc: string;

  searchValue: string;
  originalSearchValue: string;
  search$ = new Subject<string>();
  pendingInitialSearchTerm: string | undefined;

  sections: DocumentViewSection[];
  isSmartSummaryDisabled: boolean;
  smartSummaryTooltip;
  originalSections: DocumentViewSection[];
  _sectionFilter: SectionFilter;
  documentScoringMetricsSettings: DocumentScoringMetricsOvertimeSettings | undefined;

  private session: Session;
  private dataSourceType: DataSourceType;
  private analysisGroupId: string;
  private isSocial: boolean;

  showScoringMetrics = false;
  canShowNlpData = true;
  canShowMetadata = true;
  showNlpOrMetadata = true;
  showPii = false;
  piiToggle = false;
  showOriginalLanguage = false;
  originalLanguageToggle = false;
  showSmartSummary = false;
  smartSummaryToggle = false;
  lastSmartSummaryError: string;

  collectionMap = new Map<string, Collection>();
  contentChannelTreeMap = new Map<string, ContentChannel[]>();

  isMetadataExpanded = true;
  showTagInput = false;
  tagSeparatorKeysCodes: number[] = [ENTER];
  tagFormControl = new UntypedFormControl();
  autocompleteTags: Observable<string[]>;
  isTagCompletionInProgress = false;
  isTagsDisplaying = true;

  editorState: 'none' | 'sentiment' | 'entity' | 'topic' = 'none';

  entityToEdit: any;
  topicToEdit: any;
  selectionChangeListener: () => void;
  selection: {
    text?: string;
    begin?: number;
    end?: number;
  } = {};
  documentAudioSrc: string;
  showAudioPlayer: boolean;
  isPlaybackDisabled = true;
  audioCurrentTime = undefined;

  ES_MAX_DOCUMENT_SEARCH_LIMIT = 10000;
  private _summaryTextSizeMin: number;

  viewType: 'default' | 'conversational' = 'default';
  speakerColorIndices: { [key: string]: string } = {};

  constructor(
    private accountService: SemantriaAccount,
    private storageAccountService: StorageAccountService,
    private documentService: DocumentService,
    private alertService: AlertService,
    private authService: AuthService,
    private dialogService: DialogService,
    private filterService: FilterService,
    private tagCacheService: TagCacheService,
    private annotationService: AnnotationService,
    private nlpOutputService: NlpOutputService,
    private collectionService: CollectionService,
    private contentChannelService: ContentChannelService,
    private cdr: ChangeDetectorRef,
    private lexDatePipe: LexDatePipe,
    private logger: LoggerService,
    @Inject(DOCUMENT) private webDocument: Document
  ) {
    this.selectionChangeListener = () => {
      const selection = this.webDocument.getSelection();

      if (selection.type === 'None') {
        this.clearSelection();
        return;
      }

      let ancestorElement = selection.anchorNode.parentElement;
      if (selection.rangeCount === 1) {
        ancestorElement = selection.getRangeAt(0).commonAncestorContainer;

        if (ancestorElement.nodeType === Node.TEXT_NODE) {
          ancestorElement = ancestorElement.parentElement;
        }
      }

      if (ancestorElement.classList.contains('section-text')) {
        if (selection.type === 'Range') {
          this.selection = {
            text: selection.toString(),
            begin: selection.anchorOffset,
            end: selection.focusOffset,
          };
        } else {
          this.clearSelection();

          if (ancestorElement.getElementsByClassName('highlight').length) {
            this.searchDocument();
          }
        }
      } else if (
        ancestorElement.classList.contains('highlight') ||
        ancestorElement.classList.contains('selection-highlight')
      ) {
        this.clearSelection();
      } else if (this.selection) {
        this.searchDocument();
      }
    };

    this.initTagsAutocomplete();
  }

  async ngOnInit() {
    this.search$
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.searchDocument());
    this.session = await this.authService.getSession();
    this.isReadOnly = this.authService.isReadOnly;
    this.isAlwaysRedact = this.authService.isAlwaysRedact;
    this.page = this.options.page;
    const dataSource = this.options.widgetDataSource;
    this.dataSourceType = this.options.dataSourceType ?? dataSource?.type;
    this.analysisGroupId = this.options.analysisGroupId ?? dataSource?.groupId;
    this.isSocial = this.options.isSocial ?? dataSource?.isSocial;
    this.canShowNlpData = this.options.showNlpData ?? true;
    this.canShowMetadata = this.options.showMetadata ?? true;
    this.showNlpOrMetadata = this.canShowNlpData || this.canShowMetadata;
    if (this.isSocial) {
      this.isReadOnly = true;
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.options && changes.options.currentValue) {
      const prev = changes.options.previousValue as DocumentViewOptions | undefined;
      this.options = changes.options.currentValue;
      this.page = this.options.page;
      this.pageIndex = this.options.pageIndex;
      this.limit = this.options.limit;
      this.documentId = this.options.documentId;
      this.documentIndex = this.options.documentIndex;
      this.pendingInitialSearchTerm = this.options.initialSearchTerm;
      // Refresh data source context on each options change (prevents stale or undefined values)
      const dataSource = this.options.widgetDataSource;
      this.dataSourceType = this.options.dataSourceType ?? dataSource?.type;
      this.analysisGroupId = this.options.analysisGroupId ?? dataSource?.groupId;
      this.isSocial = this.options.isSocial ?? dataSource?.isSocial ?? this.isSocial;
      if (prev?.documentId === this.documentId) {
        this.createDocumentSections();
        this.cdr.detectChanges();
        return;
      }
      this.account = await this.accountService.getAccount();
      this.hasLLMFeature = AccountUtils.hasFeature(this.account, 'LARGE_LANGUAGE_MODELS');
      this._summaryTextSizeMin =
        this.account.storageLimits[DOCUMENT_SUMMARY_TEXT_SIZE_MIN] ??
        DOCUMENT_SUMMARY_TEXT_SIZE_MIN_DEFAULT;
      this.refresh();
    }
  }

  ngOnDestroy() {
    this.webDocument.removeEventListener('selectionchange', this.selectionChangeListener);
    this.search$.unsubscribe();
  }

  @HostListener('window:keydown.ArrowLeft', ['$event'])
  onArrowLeft(event: KeyboardEvent) {
    event.preventDefault();
    this.prevDocument();
  }

  @HostListener('window:keydown.ArrowRight', ['$event'])
  onArrowRight(event: KeyboardEvent) {
    event.preventDefault();
    this.nextDocument();
  }

  private createDocumentSections(selectionClass?) {
    const sections = this.nlpOutputService.createDocumentSections(
      this.document,
      this.featureCatalog,
      false,
      this.options,
      'default',
      selectionClass
    );

    const documentLength = sections.reduce((sum, s) => {
      s.value ||= Utils.escapeHtml(Constants.BLANK);
      return sum + s.value.length;
    }, 0);

    this.smartSummaryTooltip = translate('documents.view.show-smart-summary');
    this.isSmartSummaryDisabled = false;
    if (!this.hasSummaryScene) {
      this.isSmartSummaryDisabled = true;
      this.smartSummaryTooltip = translate('documents.view.no-summary-scenario');
    }
    if (documentLength < this._summaryTextSizeMin) {
      this.isSmartSummaryDisabled = true;
      this.smartSummaryTooltip = translate('documents.view.document-short-summarize');
    }
    this.sections = this.transformSectionsForView(sections);
    this.speakerColorIndices = this.calculateSpeakerColorIndices(this.sections);
  }

  private transformSectionsForView(sections: DocumentViewSection[]) {
    const viewSections: DocumentViewSection[] = sections.map((section) => ({
      ...section,
      label: this.getSectionLabel(section),
    }));

    switch (this.viewType) {
      case 'conversational':
        let focusedSpeaker = this.collection.focusedSpeakerName;
        viewSections.forEach((section, idx) => {
          if (idx && !focusedSpeaker) {
            focusedSpeaker = section.label;
          }
          section.initial = section.label?.charAt(0).toLocaleUpperCase();
          section.alignment = 'left';

          if (section.label && section.label === focusedSpeaker) {
            section.alignment = 'right';
          }
        });
    }

    return viewSections;
  }

  private calculateSpeakerColorIndices(sections: DocumentViewSection[]) {
    return sections
      .filter((s) => s.alignment === 'left')
      .reduce((indexMap, section) => {
        if (indexMap[section.label] === undefined) {
          indexMap[section.label] = Object.keys(indexMap).length;
        }
        return indexMap;
      }, {});
  }

  private createOriginalDocumentSections(selectionClass?) {
    let searchType: DocumentSearchType = 'default';
    if (this.piiToggle) {
      searchType = 'unredacted';
    }
    if (this.originalLanguageToggle) {
      searchType = 'untranslated';
    }

    this.originalSections = this.nlpOutputService.createDocumentSections(
      this.document,
      this.featureCatalog,
      false,
      this.options,
      searchType,
      selectionClass
    );
  }

  /**
   * Load title for analytic items (meta group items).
   * Searching for analytic item addressed by analytics item string identifier.
   */
  public translateKeyFor(analyticItem: string): string {
    return this.filterService.filterConfig[analyticItem].title;
  }

  getTaxonomyPath(item) {
    let path = item.title;
    if (item.path) {
      path = `${item.path.replace('|', ' / ')} / ${path}`;
    }
    return path;
  }

  /**
   * Determine data source for document retrieval with a defensive fallback if group id is missing.
   *
   * @param doc - Document to use for fallback if group id is missing
   * @returns Data source for document retrieval
   */
  private determineDataSource(doc?: Document): { type: 'collection' | 'group'; id: string } {
    if (this.dataSourceType === 'analysis') {
      return { type: 'collection', id: this.options.collectionName };
    }

    if (this.analysisGroupId) {
      return { type: 'group', id: this.analysisGroupId };
    }

    // Fallback: if group id is missing, use collection endpoint to avoid calling /analysis-groups/undefined
    // Prefer document's collectionName if available, otherwise options.collectionName
    const fallbackCollectionName = doc?.collectionName || this.options.collectionName;
    this.logger.warn(
      '[DocumentViewComponent] analysisGroupId is undefined; falling back to collection endpoint',
      {
        widgetDataSourceType: this.options?.widgetDataSource?.type,
        widgetDataSourceGroupId: this.options?.widgetDataSource?.groupId,
        optionsAnalysisGroupId: this.options?.analysisGroupId,
        fallbackCollectionName,
        documentId: this.documentId,
        projectId: this.options?.projectId,
      }
    );
    return { type: 'collection', id: fallbackCollectionName };
  }

  /**
   * Load entire document. Shows loading spinner above content during load.
   *
   * @return Promise<Document>
   */
  public async refresh() {
    this.isLoading.document = true;

    if (this.options.collectionName && !this.collectionMap.has(this.options.collectionName)) {
      try {
        const collection = await this.collectionService.getCollectionByName(
          this.options.collectionName,
          this.options.projectId,
          this.isSocial
        );

        this.collectionMap.set(collection.collectionName, collection);

        if (collection.contentChannel) {
          const contentChannel = await this.contentChannelService.getContentChannelParentTree(
            collection.contentChannel
          );
          this.contentChannelTreeMap.set(collection.contentChannel, contentChannel);
        }
      } catch (error) {
        this.alertService.showErrorToast(translate('documents.view.collection-not-found'), error);
      }
    }
    this.viewType = this.determineViewType(this.collection?.contentChannel);

    const doc = this.page.items[this.documentIndex];
    const analysisNames = doc?.analyses ? [doc.analyses[0].analysisName] : [];

    const dataSource = this.determineDataSource(doc);

    const pageItemSupplier =
      this.options.pageItemSupplier ??
      ((documentId: string) =>
        this.documentService.getById(
          dataSource,
          documentId,
          analysisNames,
          this.options.projectId,
          this.isSocial
        ));

    this.document = await pageItemSupplier(this.documentId);

    if (this.document.annotations?.length > 0) {
      const sentimentScoreAnnotation = this.annotationService.getDocumentSentiment(
        this.document.annotations
      );
      this.analysis.sentimentScore =
        sentimentScoreAnnotation?.data.sentimentModify.sentimentScore ||
        this.analysis.sentimentScore;
      this.analysis['sentimentPolarity' + this.options.sentimentScale] = !isNaN(
        this.analysis.sentimentScore
      )
        ? SentimentUtils.getSentimentValueFromRange(
            this.analysis.sentimentScore,
            this.options.sentimentRanges.document,
            this.options.sentimentScale
          )
        : this.analysis.sentimentPolarity;

      this.analysis.topics = this.annotationService.getItems(
        'topic',
        this.document.annotations,
        this.analysis.topics
      );

      this.analysis.entities = this.annotationService.getItems(
        'entity',
        this.document.annotations,
        this.analysis.entities
      );
    }

    const audioPath = this.document.audioPath;
    this.showAudioPlayer = !!audioPath;
    this.isPlaybackDisabled = !this.authService.isPlaybackAllowed; //&& !!this.analysis.redactedText;
    if (!this.isPlaybackDisabled && audioPath) {
      this.documentAudioSrc = await this.documentService.getAudioUrl(
        this.options.projectId,
        this.options.collectionName,
        this.documentId,
        audioPath
      );
    }

    this.sortTags();

    await this.storageAccountService.groupFieldsSub.latest();
    this.initCustomFields(this.document?.metadata);
    this.createDocumentSections();
    this.isLoading.document = false;

    // Toggle section by sectionId (sectionFilter can change, so it should be done before change detection)
    const currentSectionFilter = this.options.sectionFilters?.find(
      (f) => f.documentId === this.documentId
    );
    if (currentSectionFilter !== undefined) {
      this.toggleFirstMatchingSection(currentSectionFilter.sectionId);
    }

    this.cdr.markForCheck();

    if (this.pendingInitialSearchTerm) {
      this.searchDocument(this.pendingInitialSearchTerm);
      this.pendingInitialSearchTerm = undefined;

      this.cdr.markForCheck();
    }

    setTimeout(() => {
      if (
        this.options.sectionPropertySelected &&
        this.options.entity &&
        this.options.analyticItem
      ) {
        this.scrollToFirstMatchingSectionProperty();
      } else {
        const element =
          this.tagScroll?.nativeElement.getElementsByClassName('.mat-cell span.clicked')[0];
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }

  private scrollToFirstMatchingSectionProperty(): void {
    const propertyName = this.options.analyticItem.replace('sectionProperties.', '');
    const targetValue = this.options.entity;

    const matchingSectionIndex = this.sections.findIndex((section) => {
      if (propertyName === 'segment' && section.segment) {
        return section.segment.includes(targetValue);
      }

      if (propertyName === 'speakerName' && section.label) {
        return section.label === targetValue;
      }

      if (section.metadata?.[propertyName] !== undefined) {
        const metadataValue = section.metadata[propertyName];
        if (Array.isArray(metadataValue)) {
          return metadataValue.includes(targetValue);
        }
        return String(metadataValue) === targetValue;
      }

      return false;
    });

    this.scrollToSectionByIndex(matchingSectionIndex);
  }

  private scrollToSectionByIndex(sectionIndex: number) {
    if (sectionIndex >= 0) {
      const sectionElements = document.querySelectorAll('.section-wrapper');
      const targetElement = sectionElements[sectionIndex];
      targetElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private determineViewType(contentChannel: string) {
    if (!contentChannel) {
      return 'default';
    }

    const contentChannelTree = this.contentChannelTreeMap.get(contentChannel);

    const rootLabel = contentChannelTree[0]?.label.trim().toLocaleLowerCase();
    const subLabel = contentChannelTree[1]?.label.trim().toLocaleLowerCase();

    if (rootLabel === 'conversational') {
      if (subLabel.includes('summary')) {
        return 'default';
      }

      return 'conversational';
    }

    return 'default';
  }

  getSectionLabel(section: DocumentViewSection): string | undefined {
    if (section.label) {
      return section.label;
    }

    if (!section.metadata) {
      return undefined;
    }

    const sectionSeparator = this.collection?.sectionSeparator;
    if (!sectionSeparator) {
      return undefined;
    }

    return section.metadata[sectionSeparator.label];
  }

  private initCustomFields(metadata: { [key: string]: any }) {
    this.customFields = [];

    if (!metadata) {
      return;
    }

    if (!this.sectionFilter) {
      this.customFields.push(
        ...FieldService.STANDARD_FIELDS.filter(
          (f) => f.property !== 'locationId' && this.document[f.property] !== null
        ).map((f) => ({
          id: f.property,
          longLabel: f.label,
          value: this.document[f.property],
          type: f.type,
          isAlias: false,
          pipeParams: f.pipeParams,
        }))
      );
    }

    const aliasFields = FieldService.getAliasFields();

    const customMetadataFields: {
      id: string;
      longLabel: string;
      values: string[];
      type: string;
      isAlias: boolean;
    }[] = [];

    const spreadValueForAlias = (value: any, type: string): string => {
      if (Array.isArray(value)) {
        return value.map((v) => spreadValueForAlias(v, type)).join(',');
      }

      if (type === 'date') {
        return this.lexDatePipe.transform(value);
      }

      return value;
    };

    for (const [metadataFieldName, value] of Object.entries(metadata)) {
      const aliasField = aliasFields.find((a) => a.aliasedFields.includes(metadataFieldName));
      if (aliasField) {
        if (aliasField.metadata?.display === false) {
          continue;
        }

        let customMetadataField = customMetadataFields.find((c) => c.id === aliasField.fieldName);
        if (!customMetadataField) {
          customMetadataField = {
            id: aliasField.fieldName,
            longLabel: aliasField.description,
            values: [],
            type: aliasField.fieldType,
            isAlias: true,
          };

          customMetadataFields.push(customMetadataField);
        }

        if (value) {
          customMetadataField.values.push(spreadValueForAlias(value, aliasField.fieldType));
        }
      } else {
        const metadataField = FieldService.getField(metadataFieldName);

        if (
          metadataField &&
          (!metadataField.metadata || metadataField.metadata.display !== false)
        ) {
          customMetadataFields.push({
            id: metadataField.fieldName,
            longLabel: metadataField.description,
            values: [value],
            type: metadataField.fieldType,
            isAlias: false,
          });
        }
      }
    }

    this.customFields.push(
      ...customMetadataFields.map((f) => ({
        id: f.id,
        longLabel: f.longLabel,
        value: f.isAlias ? f.values.join(',') : f.values[0],
        type: f.type,
        isAlias: f.isAlias,
      }))
    );

    this.customFields = this.customFields
      .sort((f1, f2) =>
        f1.longLabel.localeCompare(f2.longLabel, undefined, { sensitivity: 'accent' })
      )
      .filter((f) => this.options.metadataFields?.find((mf) => f.id === mf.id)?.display ?? true);

    if (this.options.metadataFields) {
      const fieldOrder = this.options.metadataFields.map((f) => f.id);
      this.customFields.sort((f1, f2) => fieldOrder.indexOf(f1.id) - fieldOrder.indexOf(f2.id));
      this.isTagsDisplaying =
        this.options.metadataFields.find((f) => f.id === 'tags')?.display ?? true;
    }
  }

  sortTags() {
    if (this.document.tags) {
      this.document.tags.sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));
    }
  }

  public nextDocument() {
    if (!this.canMoveNextDocument) {
      return;
    }

    this.onGoToDocument(`${this.computedIndex + 2}`);
  }

  public prevDocument() {
    if (this.computedIndex === 0) {
      return;
    }

    this.onGoToDocument(`${this.computedIndex}`);
  }

  // documentIndex is the visual index (i.e. 1-based) of the requested document
  async onGoToDocument(documentIndex = this.goToDoc): Promise<void> {
    const docNumber = this.getGoToDocNumber(documentIndex);
    if (docNumber > this.ES_MAX_DOCUMENT_SEARCH_LIMIT) {
      return;
    }

    if (this.isLoading.page || this.editorState !== 'none' || !documentIndex) {
      return;
    }

    this.isLoading.page = true;

    this.clearViewState();

    const requestedPageIndex = Math.ceil(docNumber / this.limit) - 1;
    if (requestedPageIndex !== this.pageIndex) {
      await this.goToPage(requestedPageIndex);
    }

    const docIndex = (docNumber - 1) % this.limit;

    this.setDocumentAndRefresh(docIndex).then(() => {
      this.isLoading.page = false;
      this.cdr.detectChanges();
    });
  }

  private setDocumentAndRefresh(docIndex: number): Promise<void> {
    this.documentIndex = docIndex;
    this.currentDocumentId = this.page.items[this.documentIndex].id;
    this.options.collectionName = this.page.items[this.documentIndex].collectionName;

    return this.refresh();
  }

  private getGoToDocNumber(index = this.goToDoc): number {
    const goToDoc = parseInt(index, 10);

    if (goToDoc <= 1) {
      return 1;
    }

    if (goToDoc >= this.page.count) {
      return this.page.count;
    }

    return goToDoc;
  }

  private async goToPage(requestedPageIndex: number): Promise<void> {
    let request = this.page.request;
    while (request?.prev) {
      request = request.prev;
    }

    const params = {
      ...request?.params,
      'page-number': requestedPageIndex,
    };
    if (params['page-token']) {
      params['page-number'] = null;
    }

    const pageSupplier: (
      request?: StoragePageRequest<Document>
    ) => Promise<StorageListResult<Document>> =
      this.options.pageSupplier ??
      ((req) => this.documentService.getPage(req.endpoint, params, req.headers));

    const requestedPage = await pageSupplier({
      ...request,
      params: {
        ...params,
      },
    });
    this.page = requestedPage;
    this.pageIndex = requestedPageIndex;

    this.pageChange.emit(requestedPage);
  }
  private clearViewState() {
    if (
      this.showNlpOrMetadata ||
      this.showPii ||
      this.showOriginalLanguage ||
      this.showSmartSummary
    ) {
      this.showNlpOrMetadata = this.canShowNlpData || this.canShowMetadata;
    }

    // We should clear sectionIds only if we have a documentScoringMetricsSettings
    if (this.documentScoringMetricsSettings) {
      this.documentScoringMetricsSettings = {
        ...this.documentScoringMetricsSettings,
        sectionIds: undefined,
      };
    }
    this.sectionFilter = undefined;
    this.piiToggle = false;
    this.showPii = false;
    this.originalLanguageToggle = false;
    this.showOriginalLanguage = false;
    this.smartSummaryToggle = false;
    this.showSmartSummary = false;
    this.showTagInput = false;
    this.goToDoc = undefined;
  }

  initTagsAutocomplete() {
    this.autocompleteTags = this.tagFormControl.valueChanges.pipe(
      startWith(null),
      debounceTime(300), // don't try before a pause in typing
      tap(() => (this.isTagCompletionInProgress = true)), // mark start of fetch
      switchMap((tag) =>
        this.tagCacheService.getTags(
          this.options.collectionName,
          this.document.analyses[0].analysisName,
          this.options.projectId,
          tag
        )
      ),
      map((response) => {
        const result = [];
        for (const item of response) {
          if (!this.document.tags.includes(item.title)) {
            result.push(item.title);
          }
        }
        return result;
      }),
      tap(() => (this.isTagCompletionInProgress = false)) // mark fetch complete,
    );
  }

  autocompleteOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.viewValue;
    this.tagInput.nativeElement.value = '';
    this.tagFormControl.setValue(null);
    this.addTag(value);
    this.showTagInput = false;
  }

  closeAutocompletePanel($event, matTrigger) {
    if ($event.relatedTarget && $event.relatedTarget.nodeName.toLowerCase() !== 'mat-option') {
      matTrigger.closePanel();
    }
  }

  addTagEvent(event: MatChipInputEvent) {
    const value = event.value;

    if ((value || '').trim()) {
      if (this.addTag(value)) {
        this.tagInput.nativeElement.value = '';
        this.tagFormControl.reset();
        this.showTagInput = false;
      }
    }
  }

  addTagsButtonClicked() {
    this.showTagInput = true;
    setTimeout(() => {
      this.tagInput.nativeElement.focus();
      this.tagScroll?.nativeElement.scrollTo(0, this.tagScroll.nativeElement.scrollHeight, {
        behavior: 'smooth',
      });
    }, 10);
  }

  onTextPasted($event) {
    $event.preventDefault();

    const tagCount = this.document.tags.length;

    $event.clipboardData
      .getData('Text')
      .split('\n')
      .forEach((value) => {
        this.addTag(value);
      });

    if (tagCount !== this.document.tags.length) {
      this.saveTags(this.document.tags);
    }
  }
  /**
   * Stores user defined 'item' as string in 'this.document.tags' list.
   * If 'this.document.tags' is null or undefined it will initialize with [].
   * Clears input field.
   *
   * @param newtag User defined tag.
   * @param event HTML Input event.
   * @param save trigger save
   */
  public addTag(newtag: string, event?: any, save = true): boolean {
    let value = newtag || event.option.value;
    value = Utils.replaceNonPrintable(value.trim());

    this.document.tags = this.document.tags || [];

    if (value && this.document.tags.indexOf(value) < 0) {
      this.document.tags.push(value);
      this.sortTags();
      if (save) {
        this.saveTags(this.document.tags);
      }
    }

    return true;
  }

  public showDeleteTagDialog(tag) {
    const translations = translateObject('documents.view.delete-tag', { content: { tag } });

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.removeTag(tag);
        }
      });
  }

  /**
   * Remove user defined 'tag' from 'this.document.tags' list.
   * Called when user clicks on 'x' icon next to tag label.
   */
  public removeTag(tag: string) {
    const index = this.document.tags.indexOf(tag);
    if (index >= 0) {
      this.document.tags.splice(index, 1);
    }

    this.saveTags([tag], true);
  }

  /**
   * Send modified tag list to Storage server. Reads tags from 'this.document.tags'.
   * Shows loading spinner animation during save.
   */
  public async saveTags(tags: string[], remove = false): Promise<boolean> {
    this.isLoading.save = true;
    try {
      await this.documentService.tagDocumentsByDocIds(
        this.options.collectionName,
        [this.documentId],
        tags,
        remove,
        this.options.projectId
      );
      this.hasChanged = true;
      this.hasChangedChange.emit(this.hasChanged);
      this.tagCacheService.reload(this.options.collectionName);
      return true;
    } catch (error) {
      this.alertService.showErrorToast(translate('documents.view.unable-to-add-tag'), error);
      return false;
    } finally {
      this.isLoading.save = false;
      this.cdr.detectChanges();
    }
  }

  public showDeleteDocumentDialog() {
    this.dialogService
      .showDialog<DocumentDeleteComponent>(DocumentDeleteComponent, {}, {}, false)
      .afterClosed()
      .subscribe((result) => {
        if (result !== undefined) {
          this.addDeletedTag(result);
        }
      });
  }

  public async addDeletedTag(reason?: string) {
    this.addTag('deleted', null, false);
    if (reason) {
      this.addTag(reason, null, false);
    }

    const previousComputedIndex = this.computedIndex;
    const previousTotalCount = this.page.count ?? this.page.items?.length ?? 0;

    const saveTagsPromise = this.saveTags(this.document.tags);

    this.alertService.showToast(translate('documents.view.document-deleted'));

    const updatedTotalCount = Math.max(previousTotalCount - 1, 0);

    this.page.count = updatedTotalCount;

    if (this.page.items?.length) {
      this.page.items.splice(this.documentIndex, 1);
    }

    this.pageChange.emit(this.page);

    const previousDocumentNumber = previousComputedIndex + 1;

    let navigationAttempted = false;

    if (updatedTotalCount > 0) {
      navigationAttempted = true;
      const targetAbsoluteIndex = Math.min(previousComputedIndex, updatedTotalCount - 1);
      await this.navigateAfterDeletion(targetAbsoluteIndex);
    }

    const isSaved = await saveTagsPromise;
    if (!isSaved) {
      await this.restoreAfterFailedDeletion(previousDocumentNumber);
      return;
    }

    if (!navigationAttempted) {
      this.tagScroll?.nativeElement.scrollTo(-100, this.tagScroll.nativeElement.scrollHeight, {
        behavior: 'smooth',
      });
      this.close.emit(this.hasChanged);
    }
  }

  private async navigateAfterDeletion(targetAbsoluteIndex: number): Promise<void> {
    let absoluteIndex = targetAbsoluteIndex;

    while (absoluteIndex >= 0) {
      const targetPageIndex = Math.floor(absoluteIndex / this.limit);
      const targetDocumentIndex = absoluteIndex % this.limit;

      if (targetPageIndex !== this.pageIndex || this.page.items?.length <= targetDocumentIndex) {
        this.isLoading.page = true;
        await this.goToPage(targetPageIndex);
        this.isLoading.page = false;
      }

      if (this.page.items?.[targetDocumentIndex]) {
        await this.setDocumentAndRefresh(targetDocumentIndex);
        return;
      }

      absoluteIndex--;
    }

    this.close.emit(this.hasChanged);
  }

  private async restoreAfterFailedDeletion(documentNumber: number): Promise<void> {
    const boundedDocumentNumber = Math.max(documentNumber, 1);
    const requestedPageIndex = Math.ceil(boundedDocumentNumber / this.limit) - 1;
    const documentIndexWithinPage = (boundedDocumentNumber - 1) % this.limit;

    try {
      this.isLoading.page = true;
      await this.goToPage(requestedPageIndex);
      await this.setDocumentAndRefresh(documentIndexWithinPage);
    } finally {
      this.isLoading.page = false;
      this.pageChange.emit(this.page);
      this.cdr.detectChanges();
    }
  }

  public searchDocument($event?, original?: boolean) {
    let selectionClass = 'highlight';
    if (!original) {
      this.searchValue = $event || undefined;
      this.features = [{ title: this.searchValue, type: 'user-search' }];
    } else {
      this.originalSearchValue = $event || undefined;
      this.features = [{ title: this.originalSearchValue, type: 'user-search' }];
    }

    if (this.searchValue) {
      this.selection = undefined;
    }

    if (!original && this.selection) {
      selectionClass = 'selection-highlight';
      this.searchValue = undefined;
      this.features[0].mentions = [
        {
          locations: [
            {
              offset: Math.min(this.selection.begin, this.selection.end),
              length: Math.abs(this.selection.begin - this.selection.end),
            },
          ],
        },
      ];
    }

    if (original) {
      this.createOriginalDocumentSections(selectionClass);
    } else {
      this.createDocumentSections(selectionClass);
    }

    setTimeout(() => {
      const element = document.getElementsByClassName('highlight')[0];
      element?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  set currentDocumentId(number) {
    this.documentId = number;
    this.documentIdChange.emit(number);
  }

  cancelAllEdit() {
    switch (this.editorState) {
      case 'sentiment':
        this.cancelSentiment();
        break;
      case 'entity':
        this.cancelEntity();
        break;
      case 'topic':
        this.cancelTopic();
        break;
    }
  }

  onEditSentiment() {
    this.editorState = 'sentiment';
    this.piiToggle = false;
    this.showPii = false;
    this.originalLanguageToggle = false;
    this.showOriginalLanguage = false;
    this.smartSummaryToggle = false;
    this.showSmartSummary = false;
  }

  cancelSentiment() {
    this.editorState = 'none';
  }

  async saveSentiment(manualSentiment) {
    this.isLoading.editSentiment = true;

    try {
      const annotation = await this.annotationService.setDocumentSentiment(
        this.options.projectId,
        this.document.collectionName,
        this.documentId,
        manualSentiment.score,
        manualSentiment.comment
      );
      this.document.annotations.push(annotation);
      this.editorState = 'none';
      this.analysis.sentimentScore = manualSentiment.score;
      this.analysis[`sentimentPolarity${this.options.sentimentScale}`] = manualSentiment.value;
      this.hasChanged = true;
      this.hasChangedChange.emit(this.hasChanged);
      this.alertService.showToast(
        translate('documents.view.sentiment-set-success', { sentiment: manualSentiment.score })
      );
    } catch (error) {
      this.alertService.showErrorToast(translate('documents.view.sentiment-set-failed'), error);
    }

    this.isLoading.editSentiment = false;
    this.cdr.detectChanges();
  }

  clearSelection() {
    if (this.selection) {
      this.selection = undefined;
      this.searchDocument();
    }
  }

  cancelEntity() {
    this.editorState = 'none';
    this.clearSelection();
  }

  async saveEntity($event) {
    this.isLoading.editEntity = true;

    try {
      if ($event.id) {
        await this.annotationService.editEntity(
          this.options.projectId,
          this.document.collectionName,
          this.documentId,
          $event.comment,
          $event
        );
      } else {
        await this.annotationService.addEntity(
          this.options.projectId,
          this.document.collectionName,
          this.documentId,
          $event.comment,
          $event
        );
      }
      this.editorState = 'none';
      this.entityToEdit = undefined;
      this.selection = undefined;
      this.features = [];
      this.hasChanged = true;
      this.hasChangedChange.emit(this.hasChanged);
      this.alertService.showToast(
        $event.id
          ? translate('documents.view.entity-modified-success', { entity: $event.title })
          : translate('documents.view.entity-added-success', { entity: $event.title })
      );
      this.refresh();
    } catch (error) {
      this.alertService.showErrorToast(translate('documents.view.entity-update-failed'), error);
    }

    this.isLoading.editEntity = false;
    this.cdr.detectChanges();
  }

  async deleteEntity($event) {
    this.isLoading.editEntity = true;

    try {
      await this.annotationService.deleteEntity(
        this.options.projectId,
        this.document.collectionName,
        this.documentId,
        $event.comment,
        $event.id
      );
      this.editorState = 'none';
      this.hasChanged = true;
      this.hasChangedChange.emit(this.hasChanged);
      this.alertService.showToast(
        translate('documents.view.entity-delete-success', { entity: $event.title })
      );
      this.refresh();
    } catch (error) {
      this.alertService.showErrorToast(translate('documents.view.entity-delete-failed'), error);
    }

    this.isLoading.editEntity = false;
    this.cdr.detectChanges();
  }

  onAddTopicClicked() {
    this.editorState = 'topic';
    this.topicToEdit = undefined;
  }

  onEditTopicClicked(item) {
    this.topicToEdit = item;
    this.editorState = 'topic';
  }

  cancelTopic() {
    this.editorState = 'none';
    this.clearSelection();
  }

  async saveTopic($event) {
    this.isLoading.editTopic = true;

    try {
      if ($event.id) {
        await this.annotationService.editTopic(
          this.options.projectId,
          this.document.collectionName,
          this.documentId,
          $event.comment,
          $event
        );
      } else {
        await this.annotationService.addTopic(
          this.options.projectId,
          this.document.collectionName,
          this.documentId,
          $event.comment,
          $event
        );
      }
      this.editorState = 'none';
      this.topicToEdit = undefined;
      this.selection = undefined;
      this.features = [];
      this.hasChanged = true;
      this.hasChangedChange.emit(this.hasChanged);
      this.alertService.showToast(
        $event.id
          ? translate('documents.view.topic-modified-success', { topic: $event.title })
          : translate('documents.view.topic-added-success', { topic: $event.title })
      );
      this.refresh();
    } catch (error) {
      this.alertService.showErrorToast(translate('documents.view.topic-update-failed'), error);
    }

    this.isLoading.editTopic = false;
    this.cdr.detectChanges();
  }

  async deleteTopic($event) {
    this.isLoading.editTopic = true;

    try {
      await this.annotationService.deleteTopic(
        this.options.projectId,
        this.document.collectionName,
        this.documentId,
        $event.comment,
        $event.id
      );
      this.editorState = 'none';
      this.hasChanged = true;
      this.hasChangedChange.emit(this.hasChanged);
      this.alertService.showToast(
        translate('documents.view.topic-delete-success', { topic: $event.title })
      );
      this.refresh();
    } catch (error) {
      this.alertService.showErrorToast(translate('documents.view.topic-delete-failed'), error);
    }

    this.isLoading.editTopic = false;
    this.cdr.detectChanges();
  }

  onEntityToEditChange(entityToEdit: { editorState: any; entity: any }) {
    this.editorState = entityToEdit.editorState;
    this.entityToEdit = entityToEdit.entity;
  }

  onTopicToEditChange(topicToEdit: { editorState: any; topic: any }) {
    this.editorState = topicToEdit.editorState;
    this.topicToEdit = topicToEdit.topic;
  }

  onDocumentSectionsChange(documentSectionsChangeEvent: {
    sections: DocumentViewSection[];
    id: string;
  }) {
    documentSectionsChangeEvent.sections.forEach(
      (s) => (s.value ||= Utils.escapeHtml(Constants.BLANK))
    );

    this.sections = this.transformSectionsForView(documentSectionsChangeEvent.sections);
    this.speakerColorIndices = this.calculateSpeakerColorIndices(this.sections);

    this.cdr.detectChanges();

    setTimeout(() => {
      const element = document.getElementsByClassName('highlight')[0];

      element?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  private toggleFirstMatchingSection(sectionId: number): void {
    const matchingSectionIndex = this.sections.findIndex(
      (section) => section.sectionId === sectionId
    );
    if (matchingSectionIndex < 0) {
      return;
    }
    this.onSectionToggled(this.sections[matchingSectionIndex], matchingSectionIndex);
  }

  onSectionToggled(section: DocumentViewSection, index: number) {
    if (section.sectionId === null || document.getSelection()?.type === 'Range') {
      return;
    }

    const isInFilter = this.sectionFilter?.sectionIds.includes(section.sectionId);
    const isSectionFilter = isInFilter && this.sectionFilter?.filterType === 'section';

    if (isSectionFilter) {
      return;
    }

    this.sectionFilter = {
      label: `${index + 1}`,
      filterType: 'section',
      filterBase: section,
      sectionIds: [section.sectionId],
    };
    this.initCustomFields(section.metadata);

    setTimeout(() => {
      const element = document.getElementsByClassName('section-wrapper--selected')[0];
      element?.scrollIntoView({ behavior: 'smooth' });
    });

    // DASH-9971: Let's update ScoringMetricsSettings so the section information can be processed in the child components
    this.onScoringMetricsSettingsChanged({
      sectionIds: this.sectionFilter?.sectionIds,
    });
  }

  onScoringMetricsSettingsChanged(settings: Partial<DocumentScoringMetricsOvertimeSettings>) {
    this.documentScoringMetricsSettings = {
      ...this.documentScoringMetricsSettings,
      ...settings,
    };
  }

  onSectionLabelToggled(section: DocumentViewSection): void {
    if (document.getSelection()?.type === 'Range') {
      return;
    }

    const isInFilter = this.sectionFilter?.sectionIds.includes(section.sectionId);
    const isSameSpeakerGroupFilter = isInFilter && this.sectionFilter?.filterType !== 'section';

    if (isSameSpeakerGroupFilter) {
      return;
    }

    const assembleSectionLabel = (sec) => {
      if (!sec.label) {
        if (!this.collection?.sectionSeparator) {
          return;
        }

        return FieldService.getFieldLongName(this.collection.sectionSeparator.label);
      }

      if (sec.date) {
        return `${sec.label}: ${this.lexDatePipe.transform(sec.date, 'xiLong12')}`;
      }

      if (this.collection?.sectionSeparator) {
        return `${FieldService.getFieldLongName(this.collection.sectionSeparator.label)}: ${sec.label}`;
      }
    };

    const label = assembleSectionLabel(section);
    if (!label) {
      return;
    }

    this.sectionFilter = {
      label,
      filterType: section.label,
      filterBase: section,
      sectionIds: this.sections
        .filter((s) => this.getSectionLabel(s) === section.label)
        .map((s) => s.sectionId),
    };
  }

  onSectionFilterRemoved() {
    this.sectionFilter = undefined;
  }

  onClearSearchValue() {
    this.searchValue = undefined;
  }

  onScoringMetricsToggle() {
    this.showScoringMetrics = !this.showScoringMetrics;
    this.showNlpOrMetadata = false;
    this.piiToggle = false;
    this.showPii = false;
    this.originalLanguageToggle = false;
    this.showOriginalLanguage = false;
    this.smartSummaryToggle = false;
    this.showSmartSummary = false;
  }

  onNlpToggle() {
    this.showNlpOrMetadata = !this.showNlpOrMetadata;
    this.showScoringMetrics = false;
    this.piiToggle = false;
    this.showPii = false;
    this.originalLanguageToggle = false;
    this.showOriginalLanguage = false;
    this.smartSummaryToggle = false;
    this.showSmartSummary = false;
  }

  onPiiToggle() {
    if (this.isAlwaysRedact) {
      return;
    }

    this.piiToggle = !this.piiToggle;
    this.originalLanguageToggle = false;
    this.showScoringMetrics = false;
    this.showNlpOrMetadata = false;
    this.smartSummaryToggle = false;
    this.showSmartSummary = false;
    setTimeout(() => {
      this.createOriginalDocumentSections();
      this.showPii = this.piiToggle;
      this.cdr.detectChanges();
    }, 100);
  }

  onOriginalLanugageToggle() {
    if (this.analysis.redactedText && this.isAlwaysRedact) {
      return;
    }

    this.originalLanguageToggle = !this.originalLanguageToggle;
    this.piiToggle = false;
    this.showPii = this.originalLanguageToggle;
    this.showScoringMetrics = false;
    this.showNlpOrMetadata = false;
    this.smartSummaryToggle = false;
    this.showSmartSummary = false;
    setTimeout(() => {
      this.createOriginalDocumentSections();
      this.showOriginalLanguage = this.originalLanguageToggle;
      this.cdr.detectChanges();
    }, 100);
  }

  async onSmartSummaryToggle() {
    this.smartSummaryToggle = !this.smartSummaryToggle;

    if (this.smartSummaryToggle) {
      this.showScoringMetrics = false;
      this.showNlpOrMetadata = false;
      this.showPii = false;
      this.piiToggle = false;
      this.showOriginalLanguage = false;
      this.originalLanguageToggle = false;

      this.isLoading.documentSummary = true;

      try {
        this.lastSmartSummaryError = '';

        const summary = await this.documentService.generateSmartSummary(
          this.options.collectionName,
          this.documentId,
          this.options.projectId,
          this.analysis.analysisName
        );

        this.originalSections = this.nlpOutputService.createSmartSummarySections(summary);
      } catch (err) {
        this.originalSections = [];
        this.lastSmartSummaryError = translate('documents.view.get-smart-summary-failed');
        if (err.status === 402) {
          this.lastSmartSummaryError = translate('documents.view.no-smart-summary-credits');
        }
        this.alertService.showErrorToast(this.lastSmartSummaryError, err);
      } finally {
        this.isLoading.documentSummary = false;
      }
    }

    setTimeout(() => {
      this.showSmartSummary = this.smartSummaryToggle;
      this.cdr.detectChanges();
    }, 100);
  }

  onToggleMetadata() {
    this.isMetadataExpanded = !this.isMetadataExpanded;
  }

  get isMetadataVisible() {
    return (
      this.canShowMetadata && (!this.sectionFilter || this.sectionFilter.filterType === 'section')
    );
  }

  get sectionFilter() {
    return this._sectionFilter;
  }

  set sectionFilter(sectionFilter) {
    this._sectionFilter = sectionFilter;

    if (!this._sectionFilter) {
      this.initCustomFields(this.document?.metadata);
    }
  }

  get collection(): Collection {
    return this.collectionMap.get(this.options.collectionName);
  }

  get hasSummaryScene(): boolean {
    return !!this.collection?.summarySceneId;
  }

  get canMoveNextDocument(): boolean {
    //can't be too many, y'all
    return (
      this.indexBelowMaxLimit &&
      this.computedIndex + 1 < this.page.count &&
      this.editorState === 'none'
    );
  }

  get indexBelowMaxLimit(): boolean {
    return this.computedIndex + 1 < this.ES_MAX_DOCUMENT_SEARCH_LIMIT;
  }

  get featureCatalog() {
    return [
      // Current features (e.g. user search)
      ...this.features,
      // Document level static features
      ...(this.document.highlightLocations ?? []),
      // Options level static features
      ...(this.options.staticFeatures ?? []),
    ];
  }
}
